  // Keep the real form controls in place and render a sticky clone in body.
  let stickyPortalWrapper = null;
  let stickyStateObserver = null;
  let stickyPriceSyncHandler = null;
  let isObserving = false;
  let stickyVisible = false;
  let stickyToggleTimeout = null;
  let rafVisibilitySync = null;
  let stickyBaseGreenColor = '';

  function isLoadingState(control) {
    if (!control) return false;
    const ariaBusy = control.getAttribute('aria-busy') === 'true';
    const disabled = control.disabled === true;
    const className = (control.className || '').toString();
    const hasLoadingClass = /loading|adding|is-loading|btn--loading/i.test(className);
    return ariaBusy || disabled || hasLoadingClass;
  }

  function removeStickyClone() {
    if (stickyStateObserver) {
      stickyStateObserver.disconnect();
      stickyStateObserver = null;
    }
    if (stickyPriceSyncHandler) {
      document.removeEventListener('change', stickyPriceSyncHandler, true);
      document.removeEventListener('click', stickyPriceSyncHandler, true);
      stickyPriceSyncHandler = null;
    }
    if (stickyPortalWrapper && stickyPortalWrapper.parentElement) {
      stickyPortalWrapper.remove();
      stickyPortalWrapper = null;
    }
  }

  const PRICE_SYNC_SELECTORS = [
    '.product_price_section',
    '.single_product_second_price',
    '[data-price-wrapper]',
    '[data-product-price]',
    '[data-compare-price]',
    '.ymq-price-span',
    '.lma-sold-out-section',
    '.product__price',
    '.product__price--regular',
    '.product__price--compare',
    '.product__price--unit',
    '[data-price-off]',
  ];

  function copyPriceVisualState(originalButtons, cloneButtons) {
    if (!originalButtons || !cloneButtons) return;

    PRICE_SYNC_SELECTORS.forEach((selector) => {
      const realEls = originalButtons.querySelectorAll(selector);
      const cloneEls = cloneButtons.querySelectorAll(selector);

      realEls.forEach((realEl, index) => {
        const cloneEl = cloneEls[index];
        if (!cloneEl) return;

        cloneEl.className = realEl.className;
        cloneEl.innerHTML = realEl.innerHTML;

        if (realEl.hasAttribute('style')) {
          cloneEl.setAttribute('style', realEl.getAttribute('style'));
        } else {
          cloneEl.removeAttribute('style');
        }

        cloneEl.hidden = realEl.hidden;
      });
    });
  }

  function copySubmitVisualState(originalButtons, cloneButtons) {
    if (!originalButtons || !cloneButtons) return;

    // Keep sticky classes, but mirror any runtime classes toggled on the real container.
    cloneButtons.className = originalButtons.className;
    cloneButtons.classList.add('slide-in', 'lma_btn_submit');

    const realControls = originalButtons.querySelectorAll('button, input[type="submit"]');
    const cloneControls = cloneButtons.querySelectorAll('button, input[type="submit"]');

    cloneControls.forEach((cloneControl, index) => {
      const realControl = realControls[index];
      if (!realControl) return;

      cloneControl.className = realControl.className;
      cloneControl.disabled = realControl.disabled;

      const ariaDisabled = realControl.getAttribute('aria-disabled');
      if (ariaDisabled !== null) {
        cloneControl.setAttribute('aria-disabled', ariaDisabled);
      } else {
        cloneControl.removeAttribute('aria-disabled');
      }

      const ariaBusy = realControl.getAttribute('aria-busy');
      if (ariaBusy !== null) {
        cloneControl.setAttribute('aria-busy', ariaBusy);
      } else {
        cloneControl.removeAttribute('aria-busy');
      }

      if (cloneControl.tagName === 'BUTTON') {
        cloneControl.innerHTML = realControl.innerHTML;
      } else if (cloneControl.tagName === 'INPUT') {
        cloneControl.value = realControl.value;
      }

      // Mirror computed button colors so sticky text/background always match
      // the real submit control across all runtime states.
      const realStyle = window.getComputedStyle(realControl);
      if (!isLoadingState(realControl)) {
        stickyBaseGreenColor = realStyle.backgroundColor || stickyBaseGreenColor;
      }

      cloneControl.style.color = realStyle.color;
      cloneControl.style.backgroundColor = realStyle.backgroundColor;
      cloneControl.style.borderColor = realStyle.borderColor;

      // Clone-only loading overrides:
      // - loader in white
      // - text in the same green used by the regular CTA background
      if (isLoadingState(realControl)) {
        if (stickyBaseGreenColor) {
          cloneControl.style.color = stickyBaseGreenColor;
        }

        // Loader uses --button-primary-bg, so override the CSS variable
        // on the cloned control while it is in loading state.
        cloneControl.style.setProperty('--button-primary-bg', '#ffffff');

        const indeterminateLoaderEls = cloneControl.querySelectorAll('.loader-indeterminate');
        indeterminateLoaderEls.forEach((loaderEl) => {
          loaderEl.style.backgroundColor = '#ffffff';
        });
      } else {
        cloneControl.style.removeProperty('--button-primary-bg');
      }
    });

    copyPriceVisualState(originalButtons, cloneButtons);
  }

  function bindPriceSyncEvents(originalButtons, cloneButtons, holder) {
    const sync = () => copySubmitVisualState(originalButtons, cloneButtons);

    stickyPriceSyncHandler = (event) => {
      if (!stickyPortalWrapper) return;

      const target = event.target;
      if (!target || !target.closest) return;

      const isRelevant =
        target.closest('.ymq-options-box') ||
        target.closest('#lma__submit__holder') ||
        target.closest('[data-price-wrapper]') ||
        target.matches('input[name="id"], select[name="id"]');

      if (!isRelevant) return;

      sync();
      requestAnimationFrame(sync);
      setTimeout(sync, 50);
      setTimeout(sync, 200);
    };

    document.addEventListener('change', stickyPriceSyncHandler, true);
    document.addEventListener('click', stickyPriceSyncHandler, true);

    stickyStateObserver = new MutationObserver(sync);
    stickyStateObserver.observe(originalButtons, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
    stickyStateObserver.observe(holder, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function createStickyClone() {
    const originalButtons = document.querySelector('.product__submit__buttons');
    const holder = document.getElementById('lma__submit__holder');

    if (!originalButtons || !holder) return null;

    const wrapper = document.createElement('div');
    wrapper.id = 'lma__submit__holder--portal';
    wrapper.className = holder.className;

    const cloneButtons = originalButtons.cloneNode(true);
    cloneButtons.classList.add('slide-in', 'lma_btn_submit');
    wrapper.appendChild(cloneButtons);
    copySubmitVisualState(originalButtons, cloneButtons);
    bindPriceSyncEvents(originalButtons, cloneButtons, holder);

    // Clicking sticky clone forwards to the real submit control inside the original form.
    const cloneSubmitControls = cloneButtons.querySelectorAll('button, input[type="submit"]');
    cloneSubmitControls.forEach((control) => {
      control.addEventListener('click', (event) => {
        event.preventDefault();
        const realSubmit = originalButtons.querySelector('button[type="submit"], input[type="submit"]');
        if (realSubmit) {
          realSubmit.click();
        }
      });
    });

    return wrapper;
  }

  function setStickyVisible(shouldShowSticky, buttonHeight) {
    const nextVisible = Boolean(shouldShowSticky);
    if (stickyVisible === nextVisible) return;

    stickyVisible = nextVisible;
    if (stickyToggleTimeout) {
      clearTimeout(stickyToggleTimeout);
      stickyToggleTimeout = null;
    }

    const $interactObserver = $('#interact-observer');

    if (!nextVisible) {
      stickyToggleTimeout = setTimeout(() => {
        $interactObserver.css('height', '');
        removeStickyClone();
      }, 1);
      return;
    }

    stickyToggleTimeout = setTimeout(() => {
      $interactObserver.css('height', buttonHeight + 'px');
      if (!stickyPortalWrapper || stickyPortalWrapper.parentElement !== document.body) {
        stickyPortalWrapper = createStickyClone();
        if (stickyPortalWrapper) {
          document.body.appendChild(stickyPortalWrapper);
        }
      }
    }, 300);
  }

  function computeIsIntersectingByRect(targetEl) {
    if (!targetEl) return true;
    const rect = targetEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < viewportHeight && rect.bottom > 0;
  }

  function syncStickyByRectFallback() {
    if (!stickyMediaQuery.matches || !element) return;
    const $button = $('.product__submit__buttons').first();
    if (!$button.length) return;
    setStickyVisible(!computeIsIntersectingByRect(element), $button.outerHeight());
  }

  function requestSyncStickyByRectFallback() {
    if (rafVisibilitySync) return;
    rafVisibilitySync = requestAnimationFrame(() => {
      rafVisibilitySync = null;
      syncStickyByRectFallback();
    });
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const $button = $('.product__submit__buttons').first();
        if (!$button.length) return;

        setStickyVisible(!entry.isIntersecting, $button.outerHeight());
      });
    },
    {
      root: null,
      threshold: [0.1, 0.5, 0.9],
    }
  );

  const stickyMediaQuery = window.matchMedia('(max-width: 479px)');
  const element = document.querySelector('#interact-observer');

  function resetStickyState() {
    const $interactObserver = $('#interact-observer');
    if (stickyToggleTimeout) {
      clearTimeout(stickyToggleTimeout);
      stickyToggleTimeout = null;
    }
    if (rafVisibilitySync) {
      cancelAnimationFrame(rafVisibilitySync);
      rafVisibilitySync = null;
    }
    stickyVisible = false;
    removeStickyClone();
    $interactObserver.css('height', '');
  }

  function syncObserverByViewport() {
    if (!element) return;

    if (stickyMediaQuery.matches) {
      if (!isObserving) {
        observer.observe(element);
        isObserving = true;
      }
      requestSyncStickyByRectFallback();
    } else if (isObserving) {
      observer.unobserve(element);
      isObserving = false;
      resetStickyState();
    } else {
      resetStickyState();
    }
  }

  function bindMediaQueryChange(mediaQueryList, handler) {
    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handler);
    } else if (typeof mediaQueryList.addListener === 'function') {
      mediaQueryList.addListener(handler);
    }
  }

  if (element) {
    syncObserverByViewport();
    bindMediaQueryChange(stickyMediaQuery, syncObserverByViewport);
    window.addEventListener('scroll', requestSyncStickyByRectFallback, { passive: true });
    window.addEventListener('resize', requestSyncStickyByRectFallback);
    window.addEventListener('orientationchange', requestSyncStickyByRectFallback);
  }

  document.addEventListener('DOMContentLoaded', function () {
    const checkInterval = 100; // Check every 100 milliseconds
    const timeout = 1000; // Stop checking after 1000 milliseconds (1 second)
    let elapsedTime = 0;

    // 'complex' = something on this page can change what the customer pays,
    // so the blue total-price box must show. 'simple' = plain add-to-cart,
    // no box. To make a new case show the box, add it here as another
    // 'complex' return.
    function productPricingComplexity() {
      const hasYmqOptions = document.querySelector('.ymq-options-box');
      const hasQuantityPicker = document.querySelector('form[action*="/cart/add"] input[name="quantity"]');

      let hasMultipleVariants = false;
      const productJsonElement = document.querySelector('[data-product-json]');
      if (productJsonElement) {
        const product = JSON.parse(productJsonElement.textContent);
        hasMultipleVariants = product.variants && product.variants.length > 1;
      }

      if (hasYmqOptions) return 'complex'; // YMQ offers change the price
      if (hasQuantityPicker) return 'complex'; // quantity changes the total (includes flooring calculators)
      if (hasMultipleVariants) return 'complex'; // plain variant change; may move to simple later
      return 'simple';
    }

    const intervalId = setInterval(() => {
      // Polled because YMQ builds its options box after page load
      const complexity = productPricingComplexity();

      if (complexity === 'complex' || elapsedTime >= timeout) {
        clearInterval(intervalId); // Stop checking

        const lmaSubmitHolders = document.querySelectorAll('#lma__submit__holder');
        lmaSubmitHolders.forEach((lmaSubmitHolder) => {
          if (complexity === 'complex') {
            lmaSubmitHolder.classList.add('lma_has_variant');
          } else {
            lmaSubmitHolder.classList.add('lma_has_no_variant');
            lmaSubmitHolder.classList.add('variant_enabled');
          }
        });
      }

      elapsedTime += checkInterval;
    }, checkInterval);
  });